import { PropsWithChildren } from 'react';

import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Box, Button, DialogActions, Divider, LinearProgress, Stack, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { PopOver } from 'common/components/PopOver';
import { MAX_ENTITIES_NUMBER_PER_REQUEST } from 'common/constants/exportData';

import { selectPaginationGridData } from '../PalmDataGrid/palmDataGridSlice';

import { selectExportProgress, selectIsDialogOpen, setExportDialogOpen } from './exportDialogSlice';

interface IExportDialogProps {
    cancelExportCallback: ()=> void;
    customTitle?:string;
    customWarningMessage?: string;
}

const DEFAULT_TITLE = 'Exporting...';
const DEFAULT_WARNING_MESSAGE = 'If data is modified during the export generations, results may be inaccurate or incomplete.';
const BUTTON_TEST_ID = 'cancel-export-data-grid-button-test-id';

export const ExportDialog = (props: PropsWithChildren<IExportDialogProps>) => {
    const dispatch = useAppDispatch();
    const { cancelExportCallback, customTitle, customWarningMessage } = props;
    const dialogIsOpen = useAppSelector(selectIsDialogOpen);
    const paginationGridData = useAppSelector(selectPaginationGridData);
    const exportProgress = useAppSelector(selectExportProgress);
    const shouldDisplayWarningMessage = paginationGridData?.rowsCount && paginationGridData?.rowsCount > MAX_ENTITIES_NUMBER_PER_REQUEST;
    const warningMessage = customWarningMessage ?? DEFAULT_WARNING_MESSAGE;

    const totalRecords = exportProgress?.totalRecords;
    const loadedRecords = exportProgress?.loadedRecords ?? 0;
    const hasTotalRecords = totalRecords !== undefined && totalRecords > 0;
    const progressPercentage = hasTotalRecords
        ? Math.round((loadedRecords / totalRecords) * 100)
        : undefined;

    const onExportDialogClose = () => {
        cancelExportCallback();
        dispatch(setExportDialogOpen(false));
    };

    return (
        <PopOver
            title={customTitle ?? DEFAULT_TITLE}
            open={dialogIsOpen}
            showCloseIcon={false}
        >
            <Divider sx={{ borderColor: 'transparent' }}/>
            <Typography
                textAlign='center'
                color="info.light"
                sx={{ paddingTop: '0.7rem', maxWidth: '20rem' }}>
                { shouldDisplayWarningMessage ? warningMessage : ''}
            </Typography>
            <Box sx={{
                backgroundColor: 'primary.accent',
                height: '3rem',
                width: '3rem',
                borderRadius: '100%',
                position: 'absolute',
                left: '50%',
                transform: 'translate(-50%, -50%)',
            }}>
                <FileDownloadOutlinedIcon fontSize='large'
                    sx={{
                        color: 'common.white',
                        marginTop: '7px',
                        marginLeft: '6px'
                    }}
                />
            </Box>
            {exportProgress && (
                <Box sx={{ px: 2, mt: 2, textAlign: 'center' }}>
                    <LinearProgress
                        variant={hasTotalRecords ? 'determinate' : 'indeterminate'}
                        value={progressPercentage}
                        sx={{ height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {hasTotalRecords
                            ? `${loadedRecords.toLocaleString()} of ${totalRecords.toLocaleString()} records (${progressPercentage}%)`
                            : `${loadedRecords.toLocaleString()} records loaded`}
                    </Typography>
                </Box>
            )}
            <Divider sx={{ borderColor: 'transparent' }}/>
            <DialogActions sx={{ display: 'flex', justifyContent: 'center' }}>
                <Stack direction="row" spacing={2} sx={{ padding: '1rem 0' }}>
                    <Button variant="outlined"
                        onClick={onExportDialogClose}
                        data-testid={BUTTON_TEST_ID}>
                        Cancel
                    </Button>
                </Stack>
            </DialogActions>
        </PopOver>
    );
};
